import React, { useEffect, useState } from "react";
import Index from "../../../Index";
import "../../../../assets/css/global.css";
import { useDispatch } from "react-redux";
import {
  getSahayDashboardDetails,
  getSahayTypeList,
} from "../../../../redux/features/AddService";
import PagesIndex from "../../../PagesIndex";
import PageLoading from "../../../../components/common/pageLoading/PageLoading";
import { Autocomplete } from "@mui/material";

const SahayDashboard = () => {
  const navigate = PagesIndex.useNavigate();
  const location = PagesIndex.useLocation();
  const rollData = JSON.parse(localStorage?.getItem("RollName"))?.toLowerCase();
  const dispatch = useDispatch();
  const currentSahayType = location?.state?.sahayType;
  const returnedFilterYear = location?.state?.filterYear;

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month >= 4 ? `${year} - ${year + 1}` : `${year - 1} - ${year}`;
  };

  const [dashboardData, setDashboardData] = useState(null);
  const [yearsData, setYearsData] = useState([]);
  const [sahayTypeList, setSahayTypeList] = useState([]);
  // Initialize filters from location state or defaults
  const [filterYear, setFilterYear] = useState(
    returnedFilterYear || getCurrentFinancialYear()
  );
  const [filterSahayCategory, setFilterSahayCategory] = useState(
    currentSahayType || ""
  );
  const [loader, setLoader] = useState(true);

  const getDashBoardListData = async ({
    sahayCategory = "",
    year = filterYear,
  } = {}) => {
    setLoader(true);
    try {
      const res = await getSahayDashboardDetails({ sahayCategory, year });
      if (res?.status === 200) {
        setDashboardData(res?.result);
        setYearsData(res?.years || []);
      } else {
        setDashboardData(null);
        setYearsData([]);
      }
    } catch (e) {
      console.error("Error fetching dashboard data:", e);
      setDashboardData(null);
      setYearsData([]);
    } finally {
      setLoader(false);
    }
  };

  const fetchSahayTypeList = () => {
    dispatch(getSahayTypeList()).then((res) => {
      setSahayTypeList(res?.payload || []);
    });
  };

  useEffect(() => {
    getDashBoardListData({
      sahayCategory: filterSahayCategory,
      year: filterYear,
    });
    fetchSahayTypeList();
  }, []);

  useEffect(() => {
    getDashBoardListData({
      sahayCategory: filterSahayCategory,
      year: filterYear,
    });
  }, [filterYear, filterSahayCategory]);

  const handleNavigation = (status) => {
    const activeTab = filterYear === getCurrentFinancialYear() ? 1 : 0;
    navigate(`/${rollData || "staff"}/sahay-dashboard-list`, {
      state: {
        status,
        sahayType: filterSahayCategory,
        filterYear: filterYear, // Pass current filter year
        activeTab,
      },
    });
  };

  const isCurrentYear = filterYear === getCurrentFinancialYear();

  const DashboardCard = ({
    imageClass,
    imageSrc,
    imageAlt,
    label,
    count,
    status,
    additionalInfo = null,
  }) => (
    <Index.Grid
      item
      lg={additionalInfo ? 6 : 4}
      md={additionalInfo ? 6 : 4}
      sm={6}
      xs={12}
    >
      <Index.Box
        className="admin-dashboard-box"
        sx={{
          cursor: isCurrentYear ? "pointer" : "not-allowed",
          opacity: isCurrentYear ? 1 : 0.7,
          transition: "all 0.3s ease",
          "&:hover": isCurrentYear
            ? {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
              }
            : {},
        }}
        onClick={() => isCurrentYear && handleNavigation(status)}
      >
        <Index.Box className="admin-dashboard-inner-content">
          <Index.Box className={imageClass}>
            <img src={imageSrc} alt={imageAlt} />
          </Index.Box>
          <Index.Box sx={{ flex: 1 }}>
            <Index.Box>
              <Index.Typography
                variant="label"
                component="label"
                className="input-label"
              >
                {label}
              </Index.Typography>
              <Index.Typography
                variant="label"
                component="label"
                className="input-label-number"
              >
                {count}
              </Index.Typography>
            </Index.Box>
            {additionalInfo}
          </Index.Box>
        </Index.Box>
      </Index.Box>
    </Index.Grid>
  );

  return (
    <>
      {loader ? (
        <PageLoading />
      ) : (
        <Index.Box className="">
          <Index.Typography className="head-title" component="h1">
            Dashboard
          </Index.Typography>

          {/* Filters Section */}
          <Index.Grid
            className="main-gap sahay-dashboard-filter"
            container
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Index.Grid item lg={4} md={4} sm={6} xs={12}>
              <Index.Box className="sahay-multiple-input-main">
                <Autocomplete
                  disablePortal
                  id="sahay-category-autocomplete"
                  className="multiple-autocomplet-control"
                  options={sahayTypeList}
                  getOptionLabel={(option) => option?.Sahay_category || ""}
                  value={
                    sahayTypeList.find(
                      (item) => item?.Id === filterSahayCategory
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFilterSahayCategory(newValue?.Id || "");
                  }}
                  renderInput={(params) => (
                    <Index.TextField {...params} label="Sahay Category" />
                  )}
                />
              </Index.Box>
            </Index.Grid>

            <Index.Grid item lg={4} md={4} sm={6} xs={12}>
              <Index.Box className="sahay-multiple-input-main">
                <Autocomplete
                  disablePortal
                  id="year-autocomplete"
                  className="multiple-autocomplet-control"
                  options={yearsData}
                  getOptionLabel={(option) => option?.year || ""}
                  value={
                    yearsData.find((item) => item?.year === filterYear) || null
                  }
                  onChange={(event, newValue) => {
                    setFilterYear(newValue?.year || getCurrentFinancialYear());
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const inputLowerCase = inputValue?.toLowerCase();
                    const uniqueYears = new Set();
                    const filteredOptions = options.filter((option) => {
                      const year = option?.year?.toString()?.toLowerCase();
                      if (uniqueYears.has(year)) return false;
                      if (year?.includes(inputLowerCase)) {
                        uniqueYears.add(year);
                        return true;
                      }
                      return false;
                    });
                    return filteredOptions.sort((a, b) => {
                      const yearA = parseInt(a.year.split("-")[0]);
                      const yearB = parseInt(b.year.split("-")[0]);
                      return yearB - yearA;
                    });
                  }}
                  renderInput={(params) => (
                    <Index.TextField {...params} label="Year" />
                  )}
                />
              </Index.Box>
            </Index.Grid>
          </Index.Grid>

          {/* Dashboard Cards */}
          <Index.Grid className="main-gap" container spacing={2}>
            <DashboardCard
              imageClass="dashboard-card-image"
              imageSrc={PagesIndex.Png.Pending}
              imageAlt="Pending"
              label="Pending"
              count={dashboardData?.countPending || 0}
              status="Pending"
            />

            <DashboardCard
              imageClass="dashboard-card-image-2"
              imageSrc={PagesIndex.Png.Received}
              imageAlt="Hold"
              label="Hold"
              count={dashboardData?.countHold || 0}
              status="Hold"
            />

            <DashboardCard
              imageClass="dashboard-card-image-3"
              imageSrc={PagesIndex.Png.Pending}
              imageAlt="Reject"
              label="Reject"
              count={dashboardData?.countRejected || 0}
              status="Rejected"
            />

            {rollData === "committee" && (
              <DashboardCard
                imageClass="dashboard-card-image-4"
                imageSrc={PagesIndex.Png.Approved}
                imageAlt="Partial Approved"
                label="Partial Approved"
                count={dashboardData?.countPartialApproved || 0}
                status="PartialApprove"
                additionalInfo={
                  <Index.Box sx={{ mt: 1 }}>
                    <Index.Typography
                      variant="label"
                      component="label"
                      className="input-label"
                    >
                      Total Amount - {dashboardData?.partialApprovedAmount || 0}
                    </Index.Typography>
                  </Index.Box>
                }
              />
            )}

            <DashboardCard
              imageClass="dashboard-card-image-4"
              imageSrc={PagesIndex.Png.Approved}
              imageAlt="Approved"
              label="Approved"
              count={dashboardData?.countApproved || 0}
              status="Approved"
              additionalInfo={
                rollData === "committee" ? (
                  <Index.Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Index.Typography
                      variant="label"
                      component="label"
                      className="input-label"
                    >
                      Total Applied Amount - {dashboardData?.appliedAmount || 0}
                    </Index.Typography>
                    <Index.Typography
                      variant="label"
                      component="label"
                      className="input-label"
                    >
                      Total Approved Amount -{" "}
                      {dashboardData?.approveAmount || 0}
                    </Index.Typography>

                    <Index.Typography
                      variant="label"
                      component="label"
                      className="input-label"
                    >
                      Total Disbursed Amount -{" "}
                      {dashboardData?.disbursedAmount || 0}
                    </Index.Typography>
                    <Index.Typography
                      variant="label"
                      component="label"
                      className="input-label"
                    >
                      Total Disbursed Pending Amount -{" "}
                      {dashboardData?.pendingAmount || 0}
                    </Index.Typography>
                  </Index.Box>
                ) : null
              }
            />
          </Index.Grid>
        </Index.Box>
      )}
    </>
  );
};

export default SahayDashboard;
